<template>
  <div>
    <LoadingIndicatorComponent v-if="isLoading" inline />
    <template v-else-if="indicative !== null">
      <!-- In another payout currency only the amount at today's rate shows; the rate is on hover -->
      <InfoTooltip
        :text="
          $t('requestInvoice.indicative_payout_explainer', {
            rate: indicative.rate,
            date: indicative.rateDate,
          })
        "
      >
        <span
          v-if="variant === 'stat'"
          data-test="indicative-pending-payout"
          class="d-inline-flex justify-content-center align-items-end"
        >
          <span class="h3 mx-2 mb-0 fs-6">{{
            formatMoney(indicative.amount)
          }}</span>
          <span class="fs-9 text-muted">{{ indicative.currency }}</span>
        </span>
        <span v-else data-test="indicative-pending-payout" class="fw-bold">
          {{ formatMoneyWithCurrency(indicative.amount, indicative.currency) }}
        </span>
      </InfoTooltip>
    </template>
    <div
      v-else-if="variant === 'stat'"
      data-test="pending-payout-eur"
      class="d-inline-flex justify-content-center align-items-end"
    >
      <h3 class="mx-2 mb-0 fs-6">{{ eurAmountText }}</h3>
      <div class="fs-9 text-muted">{{ currencyLabel(DEFAULT_CURRENCY) }}</div>
    </div>
    <p v-else data-test="pending-payout-eur" class="mb-0 fw-bold">
      {{ eurFigure }}
    </p>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import {
  currencyLabel,
  formatMoney,
  formatMoneyWithCurrency,
} from "@/composables/formatUtils";
import { indicativePayout } from "@/composables/money/invoiceUtils";
import { type Currency, DEFAULT_CURRENCY } from "@/money/currency_constants";
import type { IndicativePayoutRate } from "@/types";
import { computed } from "vue";

/**
 * A "Money pending" figure. For a company paid out in another currency it shows the amount owed
 * at today's ECB rate in that currency only, the rate explained on hover. A null amount reads "-".
 *
 * `stat` is the dashboard headline (number with its unit beside it); `summary` is the single bold
 * line of the Submit invoice box.
 */
const props = withDefaults(
  defineProps<{
    eurAmount: number | null;
    payoutCurrency?: Currency;
    indicativePayoutRate?: IndicativePayoutRate | null;
    isLoading?: boolean;
    variant?: "stat" | "summary";
  }>(),
  { variant: "stat" }
);

const indicative = computed(() =>
  props.eurAmount === null
    ? null
    : indicativePayout(
        props.eurAmount,
        props.payoutCurrency,
        props.indicativePayoutRate
      )
);

const eurAmountText = computed(() =>
  props.eurAmount === null ? "-" : formatMoney(props.eurAmount)
);

const eurFigure = computed(
  () => `${eurAmountText.value} ${currencyLabel(DEFAULT_CURRENCY)}`
);
</script>
